import { EDocumentType, EFinancingProgram } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { Chip, TableCell, TableRow } from '@mui/material'
import { FilteredWorksheet } from '@src/types/FilteredWorksheet'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { Dispatch, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import DocumentNote from './DocumentNote'
import UploadFileButton from './UploadFileButton'
import { canSendOrDeleteHighEstimateInvoice, useDocumentStatus, useFullNameText } from './documentTableRow-hooks'

interface Props {
  documentRequired: RequiredDocument
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  setError: Dispatch<Error>
  canEdit: boolean
  worksheet: FilteredWorksheet | null
}

const DocumentTableRow = ({
  documentRequired,
  creditApplicationId,
  financingProgramId,
  setError,
  canEdit,
  worksheet,
}: Props) => {
  const { t } = useTranslation()
  const { statusMessage, chipColor } = useDocumentStatus(documentRequired)
  const fullNameText = useFullNameText(documentRequired)

  const showUploadButton = useMemo(() => {
    if (canEdit && documentRequired.typeId !== EDocumentType.ClinicInvoice) {
      return true
    }
    if (
      worksheet?.isHighEstimate &&
      worksheet.firstPaymentOn &&
      canEdit &&
      documentRequired.typeId === EDocumentType.ClinicInvoice &&
      canSendOrDeleteHighEstimateInvoice(new Date(worksheet.firstPaymentOn))
    ) {
      return true
    }
    return false
  }, [canEdit, documentRequired.typeId, worksheet?.isHighEstimate, worksheet?.firstPaymentOn])

  return (
    <TableRow>
      <TableCell style={{ borderBottom: 'none', fontWeight: 'bold' }}>{fullNameText}</TableCell>
      <TableCell style={{ borderBottom: 'none', textAlign: 'center' }}>
        {documentRequired.requiredFrom && t(`ERequiredFrom.${documentRequired.requiredFrom}`, { ns: 'enum' })}
      </TableCell>

      <TableCell style={{ borderBottom: 'none', textAlign: 'center' }}>
        {documentRequired.requiredBeforeStep &&
          t(`ECreditApplicationStep.${documentRequired.requiredBeforeStep}`, { ns: 'enum' })}
      </TableCell>
      <TableCell style={{ borderBottom: 'none', textAlign: 'center' }}>
        {formatDate(documentRequired.createdOn)}
      </TableCell>
      <TableCell style={{ borderBottom: 'none', textAlign: 'center' }}>
        {documentRequired.receivedOn && formatDate(documentRequired.receivedOn)}
      </TableCell>
      <TableCell
        style={{
          borderBottom: 'none',
          textAlign: 'center',
        }}
      >
        <Chip
          color={chipColor}
          style={{
            fontWeight: 'bold',
          }}
          label={statusMessage}
        />
      </TableCell>
      <TableCell sx={{ maxWidth: '18rem', borderBottom: 'none' }}>
        <DocumentNote message={documentRequired.note} />
        <DocumentNote message={documentRequired.refusalReason} color={`${chipColor}.main`} />
      </TableCell>
      {showUploadButton && (
        <TableCell sx={{ borderBottom: 'none', textAlign: 'center' }}>
          <UploadFileButton
            applicantType={documentRequired.applicantType}
            financingProgramId={financingProgramId}
            typeId={documentRequired.typeId}
            subKey={documentRequired.subKey}
            status={documentRequired.status}
            creditApplicationId={creditApplicationId}
            setError={setError}
          />
        </TableCell>
      )}
    </TableRow>
  )
}

export default DocumentTableRow
